import type { AxiosInstance } from 'axios';
import { makeAuthHeaders } from './utils/make-auth-headers';

/**
 * Mirrors the Rust `UserIdentitySummary` returned by the formshive backend's
 * account-linking endpoints (`/v1/a/auth/identities`). Keep in sync with the
 * server shape.
 *
 * A magic-link account-email pseudo-entry has `id: null` and is NOT unlinkable.
 */
export interface UserIdentitySummary {
  id: string | null;
  method: 'Nostr' | 'EmailMagicLink' | 'Google' | 'GitHub' | 'Microsoft' | 'Oidc';
  display_key: string | null;
  is_primary: boolean;
  linked_via: string | null;
  created_at: string;
  last_login_at: string | null;
}

/**
 * Re-auth ("step-up") challenge returned by `/v1/a/auth/reauth/initiate`. The
 * `method` is the account's PRIMARY method and dictates how the SPA continues:
 *  - redirect methods (`oidc`/`google`/`github`/`microsoft`): `auth_url` set.
 *  - `nostr`: `challenge` set — sign client-side, then verify.
 *  - `email_magic_link`: `email_sent: true` — the emailed link resumes the flow.
 */
export interface StepUpChallenge {
  method: string;
  auth_url?: string;
  challenge?: string;
  email_sent?: boolean;
}

/** Purpose of a step-up re-auth — distinguishes link, unlink and delete flows. */
export type ReauthPurpose = 'link' | 'unlink' | 'delete';

export interface RustyConnectedAccountsProps {
  client: AxiosInstance;
}

export interface RustyConnectedAccountsSpec {
  listIdentities(accessToken: string): Promise<UserIdentitySummary[]>;
  linkOidcInitiate(accessToken: string): Promise<{ auth_url: string }>;
  reauthInitiate(purpose: ReauthPurpose, accessToken: string): Promise<StepUpChallenge>;
  reauthNostrVerify(
    purpose: ReauthPurpose,
    signedEvent: unknown,
    accessToken: string
  ): Promise<{ step_up_token: string }>;
  linkCommit(
    proofToken: string,
    stepUpToken: string,
    accessToken: string
  ): Promise<UserIdentitySummary>;
  unlinkIdentity(id: string, stepUpToken: string, accessToken: string): Promise<{ success: true }>;
  deleteAccount(
    confirmation: string,
    stepUpToken: string,
    accessToken: string
  ): Promise<{ deleted: boolean }>;
}

export class RustyConnectedAccounts implements RustyConnectedAccountsSpec {
  private client: AxiosInstance;

  constructor({ client }: RustyConnectedAccountsProps) {
    this.client = client;
  }

  async listIdentities(accessToken: string): Promise<UserIdentitySummary[]> {
    const result = await this.client.get<UserIdentitySummary[]>('/a/auth/identities', {
      headers: makeAuthHeaders(accessToken),
    });
    return result.data;
  }

  async linkOidcInitiate(accessToken: string): Promise<{ auth_url: string }> {
    const result = await this.client.post<{ auth_url: string }>(
      '/a/auth/link/oidc/initiate',
      {},
      { headers: makeAuthHeaders(accessToken) }
    );
    return result.data;
  }

  async reauthInitiate(purpose: ReauthPurpose, accessToken: string): Promise<StepUpChallenge> {
    const result = await this.client.post<StepUpChallenge>(
      '/a/auth/reauth/initiate',
      {},
      { params: { purpose }, headers: makeAuthHeaders(accessToken) }
    );
    return result.data;
  }

  async reauthNostrVerify(
    purpose: ReauthPurpose,
    signedEvent: unknown,
    accessToken: string
  ): Promise<{ step_up_token: string }> {
    const result = await this.client.post<{ step_up_token: string }>(
      '/a/auth/reauth/nostr/verify',
      { purpose, response: signedEvent },
      { headers: makeAuthHeaders(accessToken) }
    );
    return result.data;
  }

  async linkCommit(
    proofToken: string,
    stepUpToken: string,
    accessToken: string
  ): Promise<UserIdentitySummary> {
    const result = await this.client.post<UserIdentitySummary>(
      '/a/auth/link/commit',
      { link_proof_token: proofToken, step_up_token: stepUpToken },
      { headers: makeAuthHeaders(accessToken) }
    );
    return result.data;
  }

  async unlinkIdentity(
    id: string,
    stepUpToken: string,
    accessToken: string
  ): Promise<{ success: true }> {
    const result = await this.client.delete<{ success: true }>(`/a/auth/identities/${id}`, {
      headers: { ...makeAuthHeaders(accessToken), 'X-Step-Up-Token': stepUpToken },
    });
    return result.data;
  }

  async deleteAccount(
    confirmation: string,
    stepUpToken: string,
    accessToken: string
  ): Promise<{ deleted: boolean }> {
    const result = await this.client.delete<{ deleted: boolean }>('/a/account', {
      data: { confirmation },
      headers: { ...makeAuthHeaders(accessToken), 'X-Step-Up-Token': stepUpToken },
    });
    return result.data;
  }
}
